import { ShareScreenBase } from "./share-screen-base"
import { getRoomDeeplinkUrl, isMobile, isUsOrCaTimezone } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import { RoomContentCard } from "./room-content-card"
import {
  getFeedMessages,
  getInviterData,
  getMessageData,
  getRoomData,
} from "~/share/share-embedded-data"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"
import { MessageFeed } from "./components/message-feed"
import { RoomHeader } from "./components/room-header"
import { HighlightedMessage } from "./components/highlighted-message"
import { useFeedEnabled } from "./use-feed-enabled"
import { useHeaderCollapse } from "./use-header-collapse"

export const ShareMessageScreen = (): JSX.Element => {
  const messageData = getMessageData()
  const roomData = getRoomData()
  const rawFeedMessages = getFeedMessages()
  const feedEnabled = useFeedEnabled(!!rawFeedMessages)
  const feedMessages = feedEnabled ? rawFeedMessages : null

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getRoomDeeplinkUrl(appEnvironment, roomData.id)
  const title = messageData?.user?.shortDisplayName
    ? `${messageData.user.shortDisplayName} in ${roomData.name}`
    : null
  const description = "Join the conversation on ChatBCC"
  useDocumentTitleDescription(title, description)
  const { phoneValue, setPhoneValue, submitPhoneNumber } = useRoomSharePhoneSubmission()

  const roomId = roomData.id
  const roomHandle = roomData.roomHandle ?? undefined

  useScreenTracking(SCREENS.Share, {
    room_id: roomId,
    room_handle: roomHandle,
    deeplink_url: deeplinkUrl,
    message_id: messageData?.id,
  })

  const { headerExpanded, handleNearTop, handleToggleExpand } = useHeaderCollapse()

  const handleJoinRoom = useCallback(async () => {
    const successMessage =
      isMobile() || !isUsOrCaTimezone()
        ? "Download the app to join the group chat."
        : "Check your texts for a link to download the app."

    const successTitle = "You skipped the waitlist!"
    const inviterData = getInviterData()

    await submitPhoneNumber({
      successMessage,
      successTitle,
      roomId,
      trackingData: {
        unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
        inviter_id: inviterData?.id,
        inviter_username: inviterData?.username,
        room_id: roomId,
        deeplink_url: deeplinkUrl,
        room_handle: roomHandle,
      },
    })
  }, [submitPhoneNumber, roomId, deeplinkUrl, roomHandle])

  return (
    <ShareScreenBase
      analyticsProps={{
        room_id: roomId,
        room_handle: roomHandle,
      }}
      handleJoinRoom={handleJoinRoom}
      phoneValue={phoneValue}
      setPhoneValue={setPhoneValue}
      deeplinkUrl={deeplinkUrl}
      renderFeedHeader={
        feedMessages
          ? ({ desktop, logoSlot }) => (
              <RoomHeader
                roomData={roomData}
                expanded={headerExpanded}
                variant={desktop ? "full" : "compact"}
                logoSlot={logoSlot}
                onToggleExpand={handleToggleExpand}
              />
            )
          : undefined
      }
      renderAbovePhone={
        feedMessages && messageData
          ? () => <HighlightedMessage message={messageData} compact={false} />
          : undefined
      }
      renderFeedMessages={
        feedMessages
          ? ({ variant, contentPaddingTop }) => (
              <MessageFeed
                messages={feedMessages}
                onNearTop={handleNearTop}
                onAtBottom={handleToggleExpand}
                variant={variant}
                contentPaddingTop={contentPaddingTop}
                headerContent={
                  variant === "mobile" && messageData ? (
                    <HighlightedMessage message={messageData} compact />
                  ) : undefined
                }
              />
            )
          : undefined
      }
      renderContent={() =>
        roomData && messageData && <RoomContentCard roomData={roomData} className="max-w-[640px]" />
      }
    />
  )
}

export default ShareMessageScreen
